import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  formatGeoChartDayLabel,
  formatGeoChartDayTooltipTitle,
  shouldShowGeoChartDayTick,
} from '../utils/geoPresentation';
import GeoMetricTitle from './GeoMetricTitle';

const CHART_WIDTH = 994;
const CHART_HEIGHT = 168;
const PAD = { top: 16, right: 24, bottom: 40, left: 48 };
const PLOT_W = CHART_WIDTH - PAD.left - PAD.right;
const PLOT_H = CHART_HEIGHT - PAD.top - PAD.bottom;
const BAR_COLOR = '#5B9BD5';
const BAR_COLOR_HOVER = '#4A86C5';
const MIN_BAR_LABEL_HEIGHT = 22;
const MIN_BAR_LABEL_WIDTH = 26;
const BAR_LABEL_CHAR_WIDTH = 7;
const MIN_BAR_GAP = 3;
const MAX_BAR_WIDTH = 72;
const MIN_BAR_WIDTH = 2;

function getBarWidth(slotWidth) {
  if (slotWidth <= 0) return 0;
  const widthWithGap = slotWidth - MIN_BAR_GAP;
  if (widthWithGap <= 0) return MIN_BAR_WIDTH;
  return Math.max(MIN_BAR_WIDTH, Math.min(MAX_BAR_WIDTH, widthWithGap));
}

function estimateBarLabelWidth(value) {
  return String(Number(value).toLocaleString()).length * BAR_LABEL_CHAR_WIDTH + 6;
}

function shouldShowBarLabel(bar, slotWidth) {
  if (bar.height < MIN_BAR_LABEL_HEIGHT) return false;
  const neededWidth = Math.max(MIN_BAR_LABEL_WIDTH, estimateBarLabelWidth(bar.value));
  return bar.width >= neededWidth && slotWidth >= neededWidth;
}

function getNiceMaxY(maxValue) {
  if (maxValue <= 0) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(maxValue));
  const normalized = maxValue / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function getHoverBand(index, labelCount) {
  const slotWidth = labelCount > 0 ? PLOT_W / labelCount : PLOT_W;
  return {
    x: PAD.left + index * slotWidth,
    width: slotWidth,
  };
}

function toPctX(value) {
  return `${(value / CHART_WIDTH) * 100}%`;
}

function toPctY(value) {
  return `${(value / CHART_HEIGHT) * 100}%`;
}

export default function GeoCitationsChart({ labels = [], values = [], loading = false }) {
  const [hoveredDay, setHoveredDay] = useState(null);

  const { maxY, hasData, bars, yTicks } = useMemo(() => {
    const numericValues = values.map((value) => Number(value) || 0);
    const peak = Math.max(...numericValues, 0);
    const nextMaxY = getNiceMaxY(peak);
    const count = labels.length;
    const slotWidth = count > 0 ? PLOT_W / count : PLOT_W;
    const barWidth = count > 0 ? getBarWidth(slotWidth) : 0;

    const nextBars = labels.map((label, index) => {
      const value = numericValues[index] || 0;
      const height = nextMaxY > 0 ? (value / nextMaxY) * PLOT_H : 0;
      const centerX = PAD.left + index * slotWidth + slotWidth / 2;
      return {
        label,
        value,
        index,
        x: centerX - barWidth / 2,
        y: PAD.top + PLOT_H - height,
        width: barWidth,
        height,
        centerX,
        labelY: PAD.top + PLOT_H - height / 2,
        showLabel: shouldShowBarLabel(
          { height, width: barWidth, value },
          slotWidth,
        ),
      };
    });

    const nextYTicks = [0, Math.round(nextMaxY / 2), nextMaxY];

    return {
      maxY: nextMaxY,
      hasData: numericValues.some((value) => value > 0),
      bars: nextBars,
      yTicks: nextYTicks,
    };
  }, [labels, values]);

  const handleDayHover = (index, event) => {
    setHoveredDay({
      index,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const clearDayHover = () => setHoveredDay(null);

  const hoveredValue = hoveredDay != null ? bars[hoveredDay.index]?.value : null;

  return (
    <div className="report-card volume-chart-panel geo-insights-chart-card geo-insights-citations-chart-card">
      <div className="chart-panel__header">
        <GeoMetricTitle metricKey="citations_over_time">Our Citations Over Time</GeoMetricTitle>
      </div>
      <div className="chart-panel__body">
        {loading ? (
          <div className="insights-chart-area insights-chart-area--md">Loading chart…</div>
        ) : !hasData ? (
          <div className="geo-insights-empty">
            <div className="geo-insights-empty__icon" aria-hidden="true">📦</div>
            <p>No citations to display</p>
          </div>
        ) : (
          <>
            <div className="product-depth-legend geo-insights-chart-legend geo-insights-chart-legend--top">
              <div className="product-depth-legend__item">
                <span
                  className="product-depth-legend__swatch"
                  style={{ background: BAR_COLOR }}
                  aria-hidden="true"
                />
                <span className="product-depth-legend__label">Our citations</span>
              </div>
            </div>
            <div
              className="geo-citations-chart-wrap"
              onMouseLeave={clearDayHover}
            >
              <svg
                className="geo-citations-chart"
                viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                preserveAspectRatio="none"
                role="img"
                aria-label="Our citations over time"
              >
                <line
                  x1={PAD.left}
                  y1={PAD.top}
                  x2={PAD.left}
                  y2={PAD.top + PLOT_H}
                  className="geo-insights-line-chart__axis"
                />
                {yTicks.map((tick) => {
                  const y = PAD.top + PLOT_H - (tick / maxY) * PLOT_H;
                  return (
                    <line
                      key={tick}
                      x1={PAD.left}
                      y1={y}
                      x2={CHART_WIDTH - PAD.right}
                      y2={y}
                      className="geo-insights-line-chart__grid"
                    />
                  );
                })}
                {bars.map((bar) => {
                  if (bar.height <= 0) return null;
                  const isHovered = hoveredDay?.index === bar.index;
                  return (
                    <rect
                      key={`${bar.label}-${bar.index}`}
                      className="geo-citations-chart__bar"
                      x={bar.x}
                      y={bar.y}
                      width={bar.width}
                      height={bar.height}
                      fill={isHovered ? BAR_COLOR_HOVER : BAR_COLOR}
                      style={{ '--bar-index': bar.index }}
                    />
                  );
                })}
                {labels.map((label, index) => {
                  const band = getHoverBand(index, labels.length);
                  return (
                    <rect
                      key={`hover-${label}`}
                      x={band.x}
                      y={PAD.top}
                      width={band.width}
                      height={PLOT_H}
                      fill="transparent"
                      className="geo-insights-line-chart__hit"
                      onMouseEnter={(event) => handleDayHover(index, event)}
                      onMouseMove={(event) => handleDayHover(index, event)}
                    />
                  );
                })}
              </svg>
              <div className="geo-citations-chart-labels" aria-hidden="true">
                {yTicks.map((tick) => {
                  const y = PAD.top + PLOT_H - (tick / maxY) * PLOT_H;
                  return (
                    <span
                      key={tick}
                      className="geo-citations-chart-labels__y-tick chart-axis-tick-value"
                      style={{ left: toPctX(PAD.left - 8), top: toPctY(y) }}
                    >
                      {tick.toLocaleString()}
                    </span>
                  );
                })}
                {bars.map((bar) => {
                  if (!bar.showLabel) return null;
                  return (
                    <span
                      key={`bar-label-${bar.label}-${bar.index}`}
                      className="geo-citations-chart-labels__bar-value"
                      style={{ left: toPctX(bar.centerX), top: toPctY(bar.labelY) }}
                    >
                      {bar.value.toLocaleString()}
                    </span>
                  );
                })}
                {labels.map((label, index) => {
                  if (!shouldShowGeoChartDayTick(index, labels.length)) return null;
                  const x = bars[index]?.centerX ?? PAD.left;
                  return (
                    <span
                      key={label}
                      className="geo-citations-chart-labels__x-tick chart-axis-label-text"
                      style={{ left: toPctX(x), top: toPctY(CHART_HEIGHT - 10) }}
                    >
                      {formatGeoChartDayLabel(label)}
                    </span>
                  );
                })}
              </div>
            </div>
            {hoveredDay != null && labels[hoveredDay.index] && createPortal(
              <div
                className="map-hover-tooltip geo-line-chart-tooltip"
                style={{ left: hoveredDay.x, top: hoveredDay.y }}
                role="tooltip"
              >
                <div className="geo-line-chart-tooltip__date">
                  {formatGeoChartDayTooltipTitle(labels[hoveredDay.index])}
                </div>
                <ul className="geo-line-chart-tooltip__list">
                  <li className="geo-line-chart-tooltip__row">
                    <span
                      className="geo-line-chart-tooltip__swatch"
                      style={{ background: BAR_COLOR }}
                      aria-hidden="true"
                    />
                    <span className="geo-line-chart-tooltip__brand">Our citations</span>
                    <span className="geo-line-chart-tooltip__value">
                      {(hoveredValue ?? 0).toLocaleString()}
                    </span>
                  </li>
                </ul>
              </div>,
              document.body,
            )}
          </>
        )}
      </div>
    </div>
  );
}
